(() => {
  const cache = new Map();
  const config = JSON.parse(document.getElementById('fe-search-config')?.textContent || '{}');
  const normalize = value => String(value || '').normalize('NFKC').replace(/α/gi, 'a').replace(/[–—]/g, '-').replace(/\b(a[679](?:[rsc])?)[\s-]+(iii|ii|iv|vi|v|i)\b/gi, '$1$2').replace(/\s+/g, ' ').trim();
  const keyOf = value => normalize(value).normalize('NFKD').toLowerCase().replace(/ø/g, 'o').replace(/ß/g, 'ss').replace(/æ/g, 'ae').replace(/[^\p{L}\p{N}]/gu, '');
  let indexPromise;
  const index = () => {
    if (!config.indexUrl) return Promise.resolve(null);
    if (!indexPromise) {
      indexPromise = fetch(config.indexUrl, { signal: AbortSignal.timeout(6000), credentials: 'omit' })
        .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(data => data.v === 1 ? data : null)
        .catch(() => { indexPromise = null; return null; });
    }
    return indexPromise;
  };
  const shards = new Map();
  const shard = (needle, generated) => {
    let bucket = 0;
    for (const char of needle) bucket = (bucket * 31 + char.codePointAt(0)) % 8;
    if (!shards.has(bucket)) {
      const url = new URL(config.indexUrl, location.origin);
      url.pathname = url.pathname.replace('fe-search-index.json', 'fe-search-index-' + bucket + '.json');
      url.searchParams.set('v', generated);
      shards.set(bucket, fetch(url, { signal: AbortSignal.timeout(6000), credentials: 'omit' })
        .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .catch(() => { shards.delete(bucket); return null; }));
    }
    return shards.get(bucket);
  };
  const exact = async (query, mode, signal) => {
    const needle = keyOf(query);
    if (needle.length < 2 || /[:|*]/.test(query)) return '';
    const data = await index();
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const partition = data ? await shard(needle, data.generated) : null;
    const entries = partition?.generated === data?.generated ? partition?.keys[needle] : null;
    if (!entries) return '';
    const handles = [];
    for (let i = 0; i < entries.length && handles.length < 20; i += 2) {
      const handle = data.handles[entries[i]];
      if (/^[^|/?#\\\\\s]+$/u.test(handle) && !handles.includes(handle)) handles.push(handle);
    }
    if (!handles.length) return '';
    const url = new URL(config.searchUrl || window.Theme.routes.search_url, location.origin);
    url.searchParams.set('q', [mode, needle, ...handles].join('|'));
    url.searchParams.set('section_id', 'fe-search-exact');
    const timeout = AbortSignal.timeout(5000);
    const response = await fetch(url, { signal: signal ? AbortSignal.any([signal, timeout]) : timeout, credentials: 'same-origin' });
    if (!response.ok) return '';
    return response.text();
  };
  const merge = (html, extra) => {
    if (!extra) return html;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const exactDoc = new DOMParser().parseFromString(extra, 'text/html');
    const items = [...exactDoc.querySelectorAll('[data-fe-search-id]')];
    if (!items.length) return html;
    items.forEach(item => { item.dataset.feExactMatch = 'true'; });
    let group = doc.querySelector('.search-flydown--product-items');
    if (!group) {
      group = doc.createElement('div');
      group.className = 'search-flydown--product-items';
      const heading = doc.createElement('h3');
      heading.className = 'search-flydown__group-title';
      heading.textContent = 'Prodotti';
      const list = doc.createElement('ul');
      list.className = 'search-flydown__group-list';
      group.append(heading, list);
      (doc.querySelector('#shopify-section-predictive-search > div') || doc.querySelector('#shopify-section-predictive-search') || doc.body).prepend(group);
    }
    const list = group.querySelector('ul');
    const ids = new Set();
    const ordered = [...items, ...list.children].filter(item => {
      const id = item.dataset.feSearchId || item.querySelector('a')?.getAttribute('href');
      if (ids.has(id)) return false;
      ids.add(id); return true;
    }).sort((a, b) =>
      Number(b.dataset.feExactMatch === 'true') - Number(a.dataset.feExactMatch === 'true') ||
      Number(b.dataset.feAvailable === 'true') - Number(a.dataset.feAvailable === 'true')
    ).slice(0, 10);
    list.replaceChildren(...ordered);
    doc.querySelectorAll('.search-flydown__no-results').forEach(el => el.remove());
    return doc.body.innerHTML;
  };
  const get = async (terms, signal) => {
    const query = normalize(terms);
    const key = query.toLocaleLowerCase();
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const entry = cache.get(key);
    if (entry && Date.now() - entry.time < 60000) return entry.html;
    const url = new URL(window.Theme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    let nativeError;
    const native = fetch(url, { signal, credentials: 'same-origin' }).then(async response => {
      if (!response.ok) throw new Error(String(response.status));
      return response.text();
    }).catch(error => {
      if (signal?.aborted) throw error;
      nativeError = error;
      return '';
    });
    const boosted = exact(query, 'p', signal).catch(error => {
      if (signal?.aborted) throw error;
      return '';
    });
    const [html, extra] = await Promise.all([native, boosted]);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!html && !extra && nativeError) throw nativeError;
    const merged = merge(html, extra);
    cache.set(key, { html: merged, time: Date.now() });
    if (cache.size > 30) cache.delete(cache.keys().next().value);
    return merged;
  };
  window.FotoEmaSearch = { normalize, get };
  document.addEventListener('focusin', event => {
    if (event.target.matches('input[name="q"]')) index();
  });
  const initialized = new WeakSet();
  let pageController;
  const enhancePage = async () => {
    const root = document.querySelector('[data-fe-exact-results]');
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    pageController?.abort();
    const controller = new AbortController();
    pageController = controller;
    const params = new URLSearchParams(location.search);
    if ([...params.keys()].some(k => k.startsWith('filter.')) || (params.has('sort_by') && params.get('sort_by') !== 'relevance') || Number(params.get('page') || 1) > 1 || (params.has('type') && !params.get('type').split(',').includes('product'))) return;
    try {
      const html = await exact(root.dataset.query, 'g', controller.signal);
      if (!root.isConnected || controller.signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = [...doc.querySelectorAll('[data-product-item][data-fe-search-id]')];
      if (!cards.length) return;
      root.querySelector('[data-fe-exact-grid]').replaceChildren(...cards);
      const ids = new Set(cards.map(card => card.dataset.feSearchId));
      const scope = root.closest('.productgrid--outer') || root.parentElement;
      scope.querySelectorAll('[data-product-item][data-fe-search-id]').forEach(card => {
        if (!root.contains(card) && ids.has(card.dataset.feSearchId)) { card.hidden = true; card.style.display = 'none'; }
      });
      const empty = scope.querySelector('.productgrid--no-results');
      if (empty) { empty.hidden = true; empty.style.display = 'none'; }
      root.hidden = false;
      // Existing theme listeners initialize cards inserted by section rendering.
      root.dispatchEvent(new CustomEvent('theme:rimg:watch', { bubbles: true }));
    } catch (error) { /* Native Shopify results remain available on a failed enhancement. */ }
  };
  const observer = new MutationObserver(enhancePage);
  observer.observe(document.body, { childList: true, subtree: true });
  enhancePage();
})();
